import { FormEvent, useEffect, useState } from "react";
import { Role, RoleMember, api } from "../api";
import { useAuth } from "../auth";
import { RoleGrantsModal } from "../components/RoleGrantsModal";
import { ConfirmModal } from "../components/ConfirmModal";

export function RolesPage() {
  const { token } = useAuth();
  const [roles, setRoles] = useState<Role[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [grantsRole, setGrantsRole] = useState<Role | null>(null);
  const [membersRole, setMembersRole] = useState<Role | null>(null);
  const [deleteRole, setDeleteRole] = useState<Role | null>(null);
  const [editRole, setEditRole] = useState<Role | null>(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [formBusy, setFormBusy] = useState(false);
  const [formNotice, setFormNotice] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  const reload = () => {
    if (!token) return;
    api
      .listRoles(token)
      .then(setRoles)
      .catch((e) => setError(String(e?.message ?? e)));
  };

  useEffect(reload, [token]);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setFormNotice(null);
    const n = name.trim();
    if (n.length < 2) {
      setFormNotice({ tone: "err", text: "Role name must be at least 2 characters." });
      return;
    }
    setFormBusy(true);
    try {
      await api.createRole(token, { name: n, description: description.trim() || null });
      setName("");
      setDescription("");
      reload();
      setFormNotice({ tone: "ok", text: `Created role "${n}".` });
    } catch (err: unknown) {
      const message =
        typeof err === "object" && err && "message" in err ? String((err as { message?: string }).message) : String(err);
      setFormNotice({ tone: "err", text: message });
    } finally {
      setFormBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-slate-700">Roles</h3>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}

      <div className="card p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Create role</h3>
        <form className="space-y-3" onSubmit={(e) => void onCreate(e)}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Role name</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Analysts — Kepler" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Description (optional)</label>
              <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
          </div>
          {formNotice && (
            <div
              className={`text-sm rounded px-3 py-2 border ${
                formNotice.tone === "ok"
                  ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                  : "bg-red-50 text-red-800 border-red-200"
              }`}
            >
              {formNotice.text}
            </div>
          )}
          <button type="submit" className="btn-primary" disabled={formBusy}>
            {formBusy ? "Creating…" : "Create role"}
          </button>
        </form>
      </div>

      <div className="card overflow-hidden">
        {roles === null ? (
          <p className="p-4 text-sm text-slate-500">Loading…</p>
        ) : roles.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">No roles yet.</p>
        ) : (
          <table className="table-clean w-full">
            <thead>
              <tr>
                <th>id</th>
                <th>name</th>
                <th>description</th>
                <th className="text-center">members</th>
                <th className="text-center">grants</th>
                <th className="text-right">actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 align-middle">
                  <td className="font-mono text-[11px] text-slate-500">{r.id}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{r.name}</span>
                      {r.confers_admin && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                          ★ admin
                        </span>
                      )}
                      {r.is_protected && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide" title="Protected role: cannot be renamed or deleted">
                          🛡 locked
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-xs text-slate-500 max-w-[16rem] truncate" title={r.description ?? ""}>
                    {r.description || "—"}
                  </td>
                  <td className="text-center">
                    <button type="button" className="btn text-xs py-1" onClick={() => setMembersRole(r)}>
                      {r.member_count}
                    </button>
                  </td>
                  <td className="text-center font-mono text-[11px]">{r.grant_count}</td>
                  <td className="text-right space-x-2 whitespace-nowrap">
                    <button type="button" className="btn-primary text-xs py-1" onClick={() => setGrantsRole(r)}>
                      grants
                    </button>
                    <button type="button" className="btn text-xs py-1" onClick={() => { setEditRole(r); setFormNotice(null); }} disabled={r.is_protected} title={r.is_protected ? "Protected role cannot be renamed" : "Edit role"}>
                      edit
                    </button>
                    <button type="button" className="btn-danger text-xs py-1" onClick={() => setDeleteRole(r)} disabled={r.is_protected} title={r.is_protected ? "Protected role cannot be deleted" : "Delete role"}>
                      delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-slate-500">
        Assign users to roles in the <strong>Users</strong> tab (key icon). A user's schema access is the union of their
        roles' grants. The <strong>Administrator</strong> role is protected and confers portal admin.
      </p>

      {token && grantsRole && (
        <RoleGrantsModal token={token} role={grantsRole} onClose={() => setGrantsRole(null)} onSaved={() => reload()} />
      )}
      {token && editRole && (
        <EditRoleModal token={token} role={editRole} onClose={() => setEditRole(null)} onSaved={() => reload()} />
      )}
      {token && membersRole && (
        <MembersModal token={token} role={membersRole} onClose={() => setMembersRole(null)} />
      )}
      {token && deleteRole && (
        <ConfirmModal
          title="Delete role"
          tone="danger"
          confirmLabel="Delete role"
          onClose={() => setDeleteRole(null)}
          onConfirm={async () => {
            await api.deleteRole(token, deleteRole.id);
            reload();
          }}
          body={
            <>
              <p>
                Delete role <strong>{deleteRole.name}</strong>?
              </p>
              <p className="text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2 text-xs">
                Its {deleteRole.grant_count} grant(s) are removed and its {deleteRole.member_count} member(s) lose the
                access this role provided. Members and their other roles are unaffected.
              </p>
            </>
          }
        />
      )}
    </div>
  );
}

function EditRoleModal({
  token,
  role,
  onClose,
  onSaved,
}: {
  token: string;
  role: Role;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSave = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.patchRole(token, role.id, { name: name.trim(), description: description.trim() || null });
      onSaved();
      onClose();
    } catch (e: unknown) {
      const msg = typeof e === "object" && e && "message" in e ? String((e as { message?: string }).message) : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }} role="presentation">
      <div className="bg-white rounded-lg border border-slate-200 shadow-xl max-w-md w-full" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">Edit role</h3>
          <button type="button" onClick={onClose} className="btn" disabled={busy}>Close</button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
            <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50 rounded-b-lg">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn-primary" onClick={() => void onSave()} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function MembersModal({ token, role, onClose }: { token: string; role: Role; onClose: () => void }) {
  const [members, setMembers] = useState<RoleMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getRoleMembers(token, role.id)
      .then((m) => {
        if (!cancelled) setMembers(m);
      })
      .catch((e: { message?: string }) => {
        if (!cancelled) setError(e?.message ?? String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [token, role.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} role="presentation">
      <div className="bg-white rounded-lg border border-slate-200 shadow-xl max-w-md w-full max-h-[85vh] flex flex-col" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Members</h3>
            <p className="text-xs text-slate-500 mt-0.5">Role <span className="font-mono">{role.name}</span></p>
          </div>
          <button type="button" onClick={onClose} className="btn">Close</button>
        </div>
        <div className="p-4 flex-1 overflow-y-auto">
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
          {members === null ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : members.length === 0 ? (
            <p className="text-sm text-slate-500">No members. Assign users in the Users tab.</p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded border border-slate-200">
              {members.map((m) => (
                <li key={m.id} className="px-3 py-2 flex items-center gap-2">
                  <span className="font-mono text-[11px] text-slate-400">{m.id}</span>
                  <span className="text-slate-800">{m.username}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
